import React, { useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import PageSeo from '../seo/PageSeo';
import AdminLayout from '../components/AdminLayout';
import OwnerInviteCard from '../components/OwnerInviteCard';
import { useAdminInvites } from '../admin/useAdminInvites';
import {
    galiaAccentBarSx,
    galiaBodySx,
    galiaFieldSx,
    galiaHeadlineSx,
    galiaPrimaryButtonSx,
    galiaSecondaryButtonSx,
    galiaSectionLabelSx,
} from '../theme/galiaUi';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AdminInvite: React.FC = () => {
    const {
        inbox,
        localOnly,
        loading,
        error: loadError,
        create,
        setTag,
        clearTags,
        setDeposit,
        clearDeposit,
        uploadApplication,
        importApplications,
        sendPortalInvite,
        remove,
        moveToLitter,
    } = useAdminInvites();
    const actions = {
        setTag,
        clearTags,
        setDeposit,
        clearDeposit,
        uploadApplication,
        sendPortalInvite,
        remove,
    };
    const [ownerName, setOwnerName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [notes, setNotes] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [importing, setImporting] = useState(false);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);
        setNotice(null);

        const trimmedName = ownerName.trim();
        const trimmedEmail = email.trim();

        if (!trimmedName || !trimmedEmail) {
            setError('Name and email are required.');
            return;
        }

        if (!EMAIL_PATTERN.test(trimmedEmail)) {
            setError('Enter a valid email address.');
            return;
        }

        setSubmitting(true);
        try {
            const result = await create({
                ownerName: trimmedName,
                email: trimmedEmail,
                phone,
                notes,
            });
            setOwnerName('');
            setEmail('');
            setPhone('');
            setNotes('');
            if (localOnly) {
                setNotice('Saved a local card. Email is sent after you deploy and sign in with Google.');
            } else if (result.emailed) {
                setNotice(`Invite emailed to ${result.invite.email}.`);
            } else {
                setNotice(result.warning || 'Saved, email failed — try again.');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not save the invite.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <PageSeo
                title="Invite owner | Galia Standard Poodles"
                description="Invite a puppy owner to the Galia portal."
                path="/admin/invite"
                noIndex
            />
            <AdminLayout>
                <Box sx={{ maxWidth: 560 }}>
                    <Typography component="h1" sx={galiaHeadlineSx}>
                        Invite puppy owner
                    </Typography>
                    <Box sx={galiaAccentBarSx} />
                    <Typography sx={{ ...galiaBodySx, mb: 3.5 }}>
                        New Jotform submissions can create a card automatically (no portal email).
                        People who already applied: export the puppy application CSV from Jotform
                        and import it here. Use <strong>Email portal invite</strong> on a card when
                        you are ready to give them a login.
                    </Typography>

                    {(error || loadError) && (
                        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                            {error || loadError}
                        </Alert>
                    )}
                    {notice && (
                        <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2 }}>
                            {notice}
                        </Alert>
                    )}

                    <Stack component="form" spacing={1.75} onSubmit={(event) => void handleSubmit(event)} sx={{ mb: 5 }}>
                        <TextField
                            label="Owner name"
                            value={ownerName}
                            onChange={(event) => setOwnerName(event.target.value)}
                            autoComplete="name"
                            required
                            fullWidth
                            sx={galiaFieldSx}
                        />
                        <TextField
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            autoComplete="email"
                            required
                            fullWidth
                            sx={galiaFieldSx}
                        />
                        <TextField
                            label="Phone"
                            type="tel"
                            value={phone}
                            onChange={(event) => setPhone(event.target.value)}
                            autoComplete="tel"
                            fullWidth
                            sx={galiaFieldSx}
                        />
                        <TextField
                            label="Notes"
                            value={notes}
                            onChange={(event) => setNotes(event.target.value)}
                            multiline
                            minRows={3}
                            fullWidth
                            sx={galiaFieldSx}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={submitting}
                            sx={galiaPrimaryButtonSx}
                        >
                            {submitting ? 'Saving…' : 'Save invite'}
                        </Button>
                    </Stack>
                    <input
                        type="file"
                        accept=".csv,text/csv"
                        hidden
                        id="jotform-csv-import"
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = '';
                            if (!file) {
                                return;
                            }
                            setError(null);
                            setNotice(null);
                            setImporting(true);
                            void importApplications(file)
                                .then((result) => {
                                    setNotice(
                                        `Imported ${result.created} new card${
                                            result.created === 1 ? '' : 's'
                                        }${
                                            result.updated
                                                ? `, filled missing info on ${result.updated}`
                                                : ''
                                        }${
                                            result.skipped
                                                ? `, skipped ${result.skipped} already complete`
                                                : ''
                                        }.`
                                    );
                                })
                                .catch((err) => {
                                    setError(
                                        err instanceof Error
                                            ? err.message
                                            : 'Could not import that CSV.'
                                    );
                                })
                                .finally(() => {
                                    setImporting(false);
                                });
                        }}
                    />
                    <Button
                        type="button"
                        variant="outlined"
                        disabled={importing}
                        onClick={() => document.getElementById('jotform-csv-import')?.click()}
                        sx={{ ...galiaSecondaryButtonSx, mb: 5 }}
                    >
                        {importing ? 'Importing…' : 'Import Jotform CSV'}
                    </Button>

                    <Stack spacing={1.5}>
                        <Typography component="h2" sx={galiaSectionLabelSx}>
                            Invited families
                        </Typography>
                        {loading && inbox.length === 0 && (
                            <Typography sx={{ color: 'rgba(26, 26, 26, 0.55)', pb: 2 }}>
                                Loading invites…
                            </Typography>
                        )}
                        {!loading && inbox.length === 0 && (
                            <Typography sx={{ color: 'rgba(26, 26, 26, 0.55)', pb: 2 }}>
                                No unassigned invites yet.
                            </Typography>
                        )}
                        {inbox.map((invite) => (
                            <OwnerInviteCard
                                key={invite.id}
                                invite={invite}
                                actions={actions}
                                variant="invite"
                                onMoveToLitter={(litterName) => {
                                    void moveToLitter(invite.id, litterName);
                                }}
                            />
                        ))}
                    </Stack>
                </Box>
            </AdminLayout>
        </>
    );
};

export default AdminInvite;
